import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Clapperboard, Plus, RefreshCw, Settings2, Trash2, Users } from 'lucide-react'

import { deleteProduction, listProductions, type Production } from '@/api/studio-production'
import { AuthSessionExpiredError } from '@/api/authSession'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { CatalogListSkeleton } from '@/shared/components/catalog/CatalogListSkeleton'
import { CatalogListToolbar, CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { PageEmptyState, PageEmptyStateActionButton } from '@/shared/components/PageEmptyState'
import { CreateProductionModal } from '@/features/studio/modals/CreateProductionModal'
import { EditProductionModal } from '@/features/studio/modals/EditProductionModal'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { StudioCatalogCard } from '@/features/studio/components/StudioCatalogCard'
import { StudioHubPageShell } from '@/features/studio/components/StudioHubPageShell'
import { CatalogBadge } from '@/shared/components/catalog/CatalogBadge'
import { ProtoIconBtn, ProtoPagination, ProtoSegmented } from '@/features/studio/components/ui-kit'
import { useClientPagination } from '@/features/studio/hooks/useClientPagination'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { Button } from '@/shared/components/ui/button'

const HUB_PAGE_SIZE = 12
const HUB_FETCH_LIMIT = 200

function formatRelativeTime(iso: string): string {
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return iso
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return '刚刚'
  if (mins < 60) return `${mins} 分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} 小时前`
  return new Date(ts).toLocaleString()
}

export function HubPage() {
  const { t, i18n } = useTranslation()
  const [items, setItems] = useState<Production[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [filter, setFilter] = useState<'created' | 'joined' | 'all'>('all')
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [editTarget, setEditTarget] = useState<Production | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setErrorKey(null)
    try {
      const res = await listProductions(HUB_FETCH_LIMIT, 0)
      setItems(res.items)
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      setErrorKey(resolveApiErrorMessage(e, 'hub.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const visibleItems = useMemo(() => {
    if (filter === 'all') return items
    if (filter === 'created') return items.filter((p) => p.is_owner !== false)
    return items.filter((p) => p.is_owner === false)
  }, [items, filter])

  const { page, setPage, total, items: pageItems } = useClientPagination(visibleItems, HUB_PAGE_SIZE)

  useEffect(() => {
    setPage(0)
  }, [filter, setPage])

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    setErrorKey(null)
    try {
      await deleteProduction(deleteTarget)
      setDeleteTarget(null)
      await reload()
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      setErrorKey(resolveApiErrorMessage(e, 'hub.deleteFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  const errorMessage =
    errorKey === 'sessionExpired'
      ? t('studio.sessionExpired')
      : errorKey === 'featureDisabled'
        ? t('studio.featureDisabled')
        : errorKey
          ? t(errorKey, { defaultValue: errorKey })
          : null

  return (
    <>
      <StudioHubPageShell mode="production">
        <CatalogListToolbar
          filters={
            !loading && items.length > 0 ? (
              <ProtoSegmented
                options={[
                  { value: 'created' as const, label: t('hub.filterCreated') },
                  { value: 'joined' as const, label: t('hub.filterJoined') },
                  { value: 'all' as const, label: t('hub.filterAll') },
                ]}
                value={filter}
                onChange={setFilter}
              />
            ) : undefined
          }
          actions={
            <CatalogListPrimaryButton onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('hub.create')}
            </CatalogListPrimaryButton>
          }
        />

        {errorMessage ? (
          <div
            className="mb-4 flex gap-3 rounded-lg border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
            role="alert"
          >
            <AlertCircle className="h-5 w-5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p>{errorMessage}</p>
              {errorKey === 'sessionExpired' ? (
                <Link
                  to={`/auth/login?return=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="mt-2 inline-block text-xs text-rose-300 underline underline-offset-2"
                >
                  {t('common.goLogin')}
                </Link>
              ) : (
                <Button
                  type="button"
                  variant="link"
                  onClick={() => void reload()}
                  className="mt-2 h-auto gap-1 p-0 text-xs text-rose-300 underline-offset-2"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {t('common.retry')}
                </Button>
              )}
            </div>
          </div>
        ) : null}

        {loading ? (
          <CatalogListSkeleton />
        ) : visibleItems.length === 0 ? (
          <PageEmptyState icon={Clapperboard} tone="indigo" title={t('hub.emptyTitle')} subtitle={t('hub.empty')}>
            <PageEmptyStateActionButton tone="amber" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('hub.createFirst')}
            </PageEmptyStateActionButton>
          </PageEmptyState>
        ) : (
          <>
            <ul className={CATALOG_LIST_GRID_CLASS}>
              {pageItems.map((item) => (
                <StudioCatalogCard
                  key={item.public_id}
                  href={productionBasePath(item.public_id)}
                  title={item.title}
                  description={item.description?.trim() || undefined}
                  fallback={
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-indigo-500/35">
                      <Clapperboard size={36} strokeWidth={1.25} aria-hidden />
                      <span className="max-w-[80%] truncate text-2xl font-semibold text-zinc-700">
                        {item.title.slice(0, 1) || '?'}
                      </span>
                    </div>
                  }
                  badges={
                    <>
                      {item.project_type ? (
                        <CatalogBadge tone="indigo" className="absolute left-3 top-3 z-[2]">
                          {t(`production.projectType.${item.project_type}`, {
                            defaultValue: item.project_type,
                          })}
                        </CatalogBadge>
                      ) : null}
                      {item.is_owner === false ? (
                        <CatalogBadge tone="sky" className="absolute right-3 top-3 z-[2]">
                          {t('hub.filterJoined')}
                        </CatalogBadge>
                      ) : null}
                    </>
                  }
                  overlayActions={
                    item.is_owner === true ? (
                      <>
                        <ProtoIconBtn
                          aria-label={t('hub.edit')}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setEditTarget(item)
                          }}
                        >
                          <Settings2 className="h-4 w-4" />
                        </ProtoIconBtn>
                        <ProtoIconBtn
                          tone="danger"
                          aria-label={t('hub.delete')}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setDeleteTarget(item.public_id)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </ProtoIconBtn>
                      </>
                    ) : undefined
                  }
                  meta={
                    <>
                      {item.aspect_ratio ? <span>{item.aspect_ratio}</span> : null}
                      <span>{t('hub.episodeTarget', { n: item.episode_target })}</span>
                      {item.role ? (
                        <span>{t(`production.roles.${item.role}`, { defaultValue: item.role })}</span>
                      ) : null}
                      {item.member_count != null ? (
                        <span className="inline-flex items-center gap-1">
                          <Users size={12} aria-hidden />
                          {t('hub.memberCount', { n: item.member_count })}
                        </span>
                      ) : null}
                      <span>
                        {i18n.language.startsWith('zh')
                          ? formatRelativeTime(item.updated_at)
                          : t('hub.updatedAt', { time: new Date(item.updated_at).toLocaleString() })}
                      </span>
                    </>
                  }
                />
              ))}
            </ul>
            <ProtoPagination
              page={page}
              pageSize={HUB_PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              prevLabel={t('pagination.prev')}
              nextLabel={t('pagination.next')}
            />
          </>
        )}
      </StudioHubPageShell>

      <CreateProductionModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => void reload()} />

      <EditProductionModal
        open={editTarget != null}
        production={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => void reload()}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title={t('hub.deleteTitle', { defaultValue: '删除项目' })}
        message={t('hub.deleteConfirm')}
        icon={Trash2}
        destructive
        busy={deleteBusy}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (deleteBusy) return
          setDeleteTarget(null)
        }}
      />
    </>
  )
}
